import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";

const TYPE_LABEL = {
  walk_in: "Walk-In",
  appointment: "Appointment",
  be_back: "Be-Back",
  phone_up: "Phone Up",
  off_site: "Off-Site",
};

const TYPE_COLOR = {
  walk_in: "#34C759",
  appointment: "#007AFF",
  be_back: "#FFCC00",
  phone_up: "#A855F7",
  off_site: "#888",
};

const ElapsedClock = ({ start }) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const sec = Math.floor((Date.now() - new Date(start).getTime()) / 1000);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return (
    <span className="tabular font-bold">
      {h > 0 ? `${h}:` : ""}{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}
    </span>
  );
};

export default function ActivityList({ opportunities, processSteps, currentUser, onAdvance, onRequestTO, onAcceptTO, onComment, onClose, onSelect }) {
  const isManager = ["floor_manager", "sales_manager", "gsm"].includes(currentUser?.role);

  if (!opportunities?.length) {
    return (
      <div className="text-sm text-white/30 italic py-12 text-center border border-white/5 bg-[#141414]/40">
        No active opportunities. The floor is hungry.
      </div>
    );
  }

  return (
    <div className="space-y-2.5" data-testid="activity-list">
      {opportunities.map((o) => {
        const stepName = processSteps?.[o.current_step_index] || "—";
        const isMine = o.salesperson_id === currentUser?.user_id;
        const tColor = TYPE_COLOR[o.type] || "#888";

        return (
          <div
            key={o.opp_id}
            className={`bg-[#141414] border ${o.to_requested ? "border-[#FF3B30] alert-pulse" : "border-white/10"} rounded-sm overflow-hidden`}
            data-testid={`opp-card-${o.opp_id}`}
          >
            <div className="flex items-stretch min-w-0">
              <div className="w-1.5 shrink-0" style={{ backgroundColor: tColor }} />
              <div className="flex-1 min-w-0 p-3 md:p-4 space-y-3">
                {/* Header */}
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: tColor }}>
                    {TYPE_LABEL[o.type] || o.type}
                  </span>
                  <h3 className="font-display text-xl font-bold uppercase tracking-wide">
                    {o.customer_name || "Unnamed Guest"}
                  </h3>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">
                    w/ {o.salesperson_name}
                  </span>
                  <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-white/50 tabular">
                    <ElapsedClock start={o.started_at} />
                  </span>
                </div>

                {/* Vehicle */}
                {o.vehicle_of_interest && (
                  <div className="text-xs text-white/60">
                    <span className="text-white/30 mr-2 uppercase tracking-widest text-[9px]">VOI</span>
                    {o.vehicle_of_interest}
                  </div>
                )}

                {/* Process steps stepper */}
                <div className="flex items-center gap-0.5 overflow-x-auto pb-1">
                  {(processSteps || []).map((s, idx) => {
                    const done = idx < o.current_step_index;
                    const current = idx === o.current_step_index;
                    return (
                      <div key={idx} className="flex items-center gap-0.5 shrink-0">
                        <div
                          className={`px-2 h-7 flex items-center text-[10px] uppercase tracking-wider font-bold whitespace-nowrap ${
                            current
                              ? "bg-[#34C759] text-black"
                              : done
                              ? "bg-white/10 text-white/70"
                              : "bg-transparent text-white/25 border border-white/10"
                          }`}
                        >
                          {s}
                        </div>
                        {idx < (processSteps?.length || 0) - 1 && <div className="w-2 h-px bg-white/10" />}
                      </div>
                    );
                  })}
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2 pt-1">
                  {(isMine || isManager) && (
                    <Button
                      size="sm"
                      onClick={() => onAdvance(o)}
                      className="bg-[#34C759] text-black hover:bg-[#2bb850] font-bold uppercase tracking-wider text-[10px] h-8 rounded-sm"
                      data-testid={`advance-${o.opp_id}`}
                    >
                      Advance → {processSteps?.[o.current_step_index + 1] || "Done"}
                    </Button>
                  )}
                  {isMine && !o.to_requested && !o.to_accepted_by && (
                    <Button
                      size="sm"
                      onClick={() => onRequestTO(o)}
                      variant="outline"
                      className="border-[#007AFF] text-[#007AFF] hover:bg-[#007AFF]/10 font-bold uppercase tracking-wider text-[10px] h-8 rounded-sm"
                      data-testid={`request-to-${o.opp_id}`}
                    >
                      Request T.O.
                    </Button>
                  )}
                  {isManager && o.to_requested && (
                    <Button
                      size="sm"
                      onClick={() => onAcceptTO(o)}
                      className="bg-[#FF3B30] text-white hover:bg-[#cc2f26] font-bold uppercase tracking-wider text-[10px] h-8 rounded-sm alert-pulse"
                      data-testid={`accept-to-${o.opp_id}`}
                    >
                      Accept T.O.
                    </Button>
                  )}
                  {o.to_accepted_by && (
                    <span className="text-[10px] uppercase tracking-widest text-[#34C759] font-bold flex items-center px-2">
                      ✓ T.O. Logged
                    </span>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onSelect(o)}
                    className="text-white/50 hover:text-white text-[10px] uppercase tracking-wider h-8 rounded-sm"
                    data-testid={`details-${o.opp_id}`}
                  >
                    Details ({o.comments?.length || 0})
                  </Button>
                  {(isMine || isManager) && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onClose(o)}
                      className="text-white/40 hover:text-white text-[10px] uppercase tracking-wider h-8 rounded-sm ml-auto"
                      data-testid={`close-${o.opp_id}`}
                    >
                      Close Out
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
